import React, { useEffect, useRef, useState } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Link,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DownloadIcon from '@mui/icons-material/Download';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DescriptionIcon from '@mui/icons-material/Description';
import BadgeIcon from '@mui/icons-material/Badge';
import { useToast } from '../../context/ToastContext';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import { EmployeeDocumentService } from '../../services/employee-document.service';
import { EmployeeLetterService } from '../../services/employee-letter.service';
import {
    ALL_DOCUMENT_TYPES,
    EMPLOYEE_DOCUMENT_TYPE_LABELS,
    EmployeeDocumentType,
    IEmployeeDocument,
} from '../../interfaces/employee-document.interface';

interface EmployeeDocumentsProps {
    userId?: string;
    // Fix-round M-1: passed through to the letter service so the downloaded filename reflects the
    // employee's actual code instead of a raw userId (see employee-letter.service.ts for why this
    // is derived client-side rather than read from Content-Disposition).
    employeeCode?: string | null;
}

const EmployeeDocuments: React.FC<EmployeeDocumentsProps> = ({ userId, employeeCode }) => {
    const [documents, setDocuments] = useState<IEmployeeDocument[]>([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState<Partial<Record<EmployeeDocumentType, boolean>>>({});
    const [lettersLoading, setLettersLoading] = useState<{ letter?: boolean; idCard?: boolean; experience?: boolean; salary?: boolean }>({});
    const toast = useToast();
    const isAdminView = Boolean(userId);
    const hasDocumentManage = usePermission(PERMISSIONS.EMPLOYEE_DOCUMENT_MANAGE);
    const canAdminUpload = isAdminView && hasDocumentManage;
    // D5/D10: appointment letter admits PAYROLL_VIEW OR PROFILE_VIEW_ALL; the ID card requires
    // PROFILE_VIEW_ALL specifically (plus a server-side scope check this button can't express).
    const hasPayrollView = usePermission(PERMISSIONS.PAYROLL_VIEW);
    const hasProfileViewAll = usePermission(PERMISSIONS.PROFILE_VIEW_ALL);
    const canViewAppointmentLetter = isAdminView && (hasPayrollView || hasProfileViewAll);
    const canViewIdCard = isAdminView && hasProfileViewAll;

    const canViewSalaryCertificate = isAdminView && hasPayrollView;

    const runDownload = async (
        key: 'experience' | 'salary',
        action: () => Promise<void>,
        failureMessage: string
    ) => {
        setLettersLoading(prev => ({ ...prev, [key]: true }));
        try {
            await action();
        } catch (err) {
            toast.showApiError(err, failureMessage);
        } finally {
            setLettersLoading(prev => ({ ...prev, [key]: false }));
        }
    };

    const handleDownloadExperienceLetter = () => userId
        ? runDownload('experience', () => EmployeeLetterService.downloadExperienceLetter(userId, employeeCode), 'Failed to download experience letter')
        : undefined;
    const handleDownloadSalaryCertificate = () => userId
        ? runDownload('salary', () => EmployeeLetterService.downloadSalaryCertificate(userId, employeeCode), 'Failed to download salary certificate')
        : undefined;

    const handleDownloadAppointmentLetter = async () => {
        if (!userId) return;
        setLettersLoading(prev => ({ ...prev, letter: true }));
        try {
            await EmployeeLetterService.downloadAppointmentLetter(userId, employeeCode);
        } catch (err) {
            toast.showApiError(err, 'Failed to download appointment letter');
        } finally {
            setLettersLoading(prev => ({ ...prev, letter: false }));
        }
    };

    const handleDownloadIdCard = async () => {
        if (!userId) return;
        setLettersLoading(prev => ({ ...prev, idCard: true }));
        try {
            await EmployeeLetterService.downloadIdCard(userId, employeeCode);
        } catch (err) {
            toast.showApiError(err, 'Failed to download ID card');
        } finally {
            setLettersLoading(prev => ({ ...prev, idCard: false }));
        }
    };

    const fileInputRefs = useRef<Partial<Record<EmployeeDocumentType, HTMLInputElement | null>>>({});

    useEffect(() => {
        fetchDocuments();
    }, [userId]);

    const fetchDocuments = async () => {
        setLoading(true);
        try {
            const docs = userId
                ? await EmployeeDocumentService.getDocumentsByUserId(userId)
                : await EmployeeDocumentService.getMyDocuments();
            setDocuments(docs);
        } catch (err) {
            toast.showApiError(err, 'Failed to load documents');
        } finally {
            setLoading(false);
        }
    };

    const docMap = new Map(documents.map(d => [d.documentType, d]));

    const handleUploadClick = (type: EmployeeDocumentType) => {
        fileInputRefs.current[type]?.click();
    };

    const handleFileChange = (type: EmployeeDocumentType) => async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        e.target.value = '';

        setUploading(prev => ({ ...prev, [type]: true }));
        try {
            const uploaded = isAdminView && userId
                ? await EmployeeDocumentService.uploadDocumentForUser(userId, type, file)
                : await EmployeeDocumentService.uploadDocument(type, file);
            setDocuments(prev => {
                const existing = prev.find(d => d.documentType === type);
                if (existing) {
                    return prev.map(d => d.documentType === type ? uploaded : d);
                }
                return [...prev, uploaded];
            });
            toast.showSuccess('Document uploaded successfully');
        } catch (err) {
            toast.showApiError(err, 'Failed to upload document');
        } finally {
            setUploading(prev => ({ ...prev, [type]: false }));
        }
    };

    const handleDownload = async (doc: IEmployeeDocument) => {
        try {
            await EmployeeDocumentService.downloadDocument(doc.id, doc.originalFileName);
        } catch (err) {
            toast.showApiError(err, 'Failed to download document');
        }
    };

    return (
        <Box sx={{ mt: 4 }} data-help-page="users.docs.page">
            <Typography variant="h6" gutterBottom>Employee Documents</Typography>
            {(canViewAppointmentLetter || canViewIdCard || canViewSalaryCertificate) && (
                // flex-wrap so the row degrades on narrow screens
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 2 }}>
                    {canViewAppointmentLetter && (
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={lettersLoading.letter ? <CircularProgress size={16} /> : <DescriptionIcon />}
                            disabled={lettersLoading.letter}
                            data-help="users.docs.appointmentLetter"
                            onClick={handleDownloadAppointmentLetter}
                        >
                            Appointment Letter
                        </Button>
                    )}
                    {canViewAppointmentLetter && (
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={lettersLoading.experience ? <CircularProgress size={16} /> : <DescriptionIcon />}
                            disabled={lettersLoading.experience}
                            data-help="users.docs.experienceLetter"
                            onClick={handleDownloadExperienceLetter}
                        >
                            Experience Letter
                        </Button>
                    )}
                    {canViewSalaryCertificate && (
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={lettersLoading.salary ? <CircularProgress size={16} /> : <DescriptionIcon />}
                            disabled={lettersLoading.salary}
                            data-help="users.docs.salaryCertificate"
                            onClick={handleDownloadSalaryCertificate}
                        >
                            Salary Certificate
                        </Button>
                    )}
                    {canViewIdCard && (
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={lettersLoading.idCard ? <CircularProgress size={16} /> : <BadgeIcon />}
                            disabled={lettersLoading.idCard}
                            data-help="users.docs.idCard"
                            onClick={handleDownloadIdCard}
                        >
                            ID Card
                        </Button>
                    )}
                </Stack>
            )}
            <TableContainer component={Paper} variant="outlined" data-help-section="users.docs.table">
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ width: 220, fontWeight: 600 }} data-help="users.docs.col.type">Document Type</TableCell>
                            <TableCell sx={{ fontWeight: 600 }} data-help="users.docs.col.file">File</TableCell>
                            <TableCell sx={{ width: 200, fontWeight: 600 }} data-help="users.docs.col.action">Action</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {ALL_DOCUMENT_TYPES.map(type => {
                            const doc = docMap.get(type);
                            const isUploading = uploading[type];

                            return (
                                <TableRow key={type}>
                                    <TableCell>{EMPLOYEE_DOCUMENT_TYPE_LABELS[type]}</TableCell>
                                    <TableCell>
                                        {doc ? (
                                            <Link
                                                component="button"
                                                underline="hover"
                                                sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                                                data-help="users.docs.fileLink"
                                                onClick={() => handleDownload(doc)}
                                            >
                                                <AttachFileIcon fontSize="small" />
                                                {doc.originalFileName}
                                            </Link>
                                        ) : (
                                            <Typography variant="body2" color="text.secondary">
                                                Not uploaded
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <Stack direction="row" spacing={1}>
                                            {doc && (
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    startIcon={<DownloadIcon />}
                                                    data-help="users.docs.action.download"
                                                    onClick={() => handleDownload(doc)}
                                                >
                                                    Download
                                                </Button>
                                            )}
                                            {((!isAdminView && !doc) || canAdminUpload) && (
                                                isUploading ? (
                                                    <CircularProgress size={24} />
                                                ) : (
                                                    <Button
                                                        variant="contained"
                                                        size="small"
                                                        startIcon={<UploadFileIcon />}
                                                        data-help="users.docs.action.upload"
                                                        onClick={() => handleUploadClick(type)}
                                                    >
                                                        {doc ? 'Replace' : 'Upload'}
                                                    </Button>
                                                )
                                            )}
                                            <input
                                                type="file"
                                                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                                                style={{ display: 'none' }}
                                                ref={el => { fileInputRefs.current[type] = el; }}
                                                onChange={handleFileChange(type)}
                                            />
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
            {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <CircularProgress size={24} />
                </Box>
            )}
        </Box>
    );
};

export default EmployeeDocuments;
